import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ShoppingCart, User } from "lucide-react";
import { useSettings, isOpenNow } from "../../lib/useSettings";
import { useCart } from "../../lib/useCart";
import { useCustomerAuth } from "../../lib/useCustomerAuth";
import { Icon } from "../../lib/icons";
import { Button } from "../ui/button";
import { track } from "../../lib/api";
import HoursDropdownContent from "./HoursDropdownContent";

export default function Header() {
  const { settings } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);
  const hoursRef = useRef(null);
  const hoursMobileRef = useRef(null);
  const loc = useLocation();
  const navigate = useNavigate();
  const { count: cartCount, subtotal_cents, setDrawerOpen: setCartOpen } = useCart();
  const { customer, isAuthenticated } = useCustomerAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setHoursOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onClickAway = (e) => {
      if (hoursRef.current && !hoursRef.current.contains(e.target) &&
          hoursMobileRef.current && !hoursMobileRef.current.contains(e.target)) {
        setHoursOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (loc.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" });
    else { navigate("/"); setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 50); }
  };

  const s = settings || {};
  const status = isOpenNow(s.hours || []);
  const todayIdx = (new Date().getDay() + 6) % 7;
  // Dynamic menu from settings with fallback to default
  const defaultNav = [
    { to: "/", label: "Accueil" },
    { to: "/services", label: "Services" },
    { to: "/realisations", label: "Réalisations" },
    ...(s?.shop_enabled ? [{ to: "/shop", label: "Boutique" }] : []),
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
  ];
  const customItems = Array.isArray(s.header_menu_items) ? s.header_menu_items.filter(m => m.show !== false) : [];
  const nav = customItems.length > 0 ? customItems : defaultNav;

  // CTA primary (configurable)
  const ctaCfg = s.header_cta_primary || { enabled: true, label: "Devis gratuit", to: "/devis", style: "sky" };
  const ctaStyles = {
    sky:   "bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90 text-white",
    red:   "bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 text-white",
    navy:  "bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90 text-white",
    ghost: "bg-transparent border border-[color:var(--brand-navy)] text-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/5",
  };

  // Visibility flags — par défaut OFF pour le nom + baseline (header trop chargé sinon)
  const showCompanyName = s.header_show_company_name === true;
  const showBaseline    = s.header_show_baseline === true;
  const showHoursPill   = s.header_show_hours_pill !== false;
  const showAccount     = s.header_show_account !== false;
  const showCart        = s.header_show_cart !== false;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-[hsl(var(--border))] shadow-sm" : "bg-white/80 backdrop-blur-md"
      }`}
      data-testid="public-header"
    >
      <div className="max-w-7xl mx-auto px-3 md:px-8 h-16 md:h-24 flex items-center justify-between gap-1.5 md:gap-4">
        {/* Logo — débordement à moitié sur la section hero en desktop */}
        <Link to="/" onClick={handleLogoClick} className="flex items-center gap-2 md:gap-3 min-w-0 flex-shrink" data-testid="header-logo-link">
          {s.logo_url ? (
            <div className="relative flex items-center">
              <img
                src={s.logo_url}
                alt={s.company_name}
                className={`w-auto object-contain transition-all duration-300 ${
                  scrolled
                    ? "h-14 md:h-20"
                    : "h-20 md:h-36"
                } translate-y-[5%] md:translate-y-[3%] drop-shadow-[0_10px_30px_color-mix(in_srgb,var(--brand-navy)_22%,transparent)]`}
                style={{ transformOrigin: "left center" }}
              />
            </div>
          ) : (
            <div className={`relative flex items-center justify-center px-2.5 md:px-4 rounded-xl border-2 border-dashed border-[color:var(--brand-navy)]/40 bg-[color:var(--brand-navy)]/5 transition-all duration-300 ${
              scrolled ? "h-12 md:h-20" : "h-14 md:h-28"
            }`} data-testid="header-logo-placeholder">
              <span className="font-display font-black tracking-[0.12em] text-[color:var(--brand-navy)] text-[11px] md:text-lg whitespace-nowrap">VOTRE LOGO</span>
            </div>
          )}
          {(showCompanyName || showBaseline) && (
            <div className="hidden sm:block leading-tight ml-1">
              {showCompanyName && (
                <div className="font-display font-extrabold text-[hsl(var(--primary))] text-base md:text-lg tracking-tight">
                  {(() => {
                    const name = s.company_short_name || s.company_name || "VOTRE ENTREPRISE";
                    const m = name.match(/^(.*?)(AIR)(.*)$/i);
                    if (!m) return name;
                    return <>{m[1]}<span className="text-[color:var(--brand-sky)]">{m[2]}</span>{m[3]}</>;
                  })()}
                </div>
              )}
              {showBaseline && (
                <div className="text-[10px] md:text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-[0.15em]">
                  {s.company_baseline || "Plomberie · Climatisation"}
                </div>
              )}
            </div>
          )}
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((n, idx) => {
            const hasChildren = Array.isArray(n.children) && n.children.length > 0;
            if (hasChildren) return <DropdownNavItem key={n.id || n.to || idx} item={n} />;
            return (
              <NavLink
                key={n.id || n.to || idx} to={n.to} end={n.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isActive ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
          {/* Desktop hours pill */}
          {showHoursPill && (
          <div className="hidden md:block relative" ref={hoursRef}>
            <button
              onClick={() => setHoursOpen(!hoursOpen)}
              className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs font-medium transition-all ${
                status.open ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100"
              }`}
              data-testid="header-hours-toggle"
            >
              <span className={`w-2 h-2 rounded-full ${status.open ? "bg-emerald-500 pulse-ring" : "bg-sky-500"}`} />
              <span>{status.label}</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${hoursOpen ? "rotate-180" : ""}`} />
            </button>
            {hoursOpen && <HoursDropdown s={s} todayIdx={todayIdx} status={status} />}
          </div>
          )}

          {/* Mobile compact hours pill — between logo and menu button (only from sm, hidden on very small screens) */}
          {showHoursPill && (
          <div className="hidden sm:block md:hidden relative" ref={hoursMobileRef}>
            <button
              onClick={() => setHoursOpen(!hoursOpen)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[10px] font-semibold transition-all whitespace-nowrap ${
                status.open ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-sky-300 bg-sky-50 text-sky-700"
              }`}
              aria-label="Horaires d'ouverture"
              data-testid="header-mobile-hours-toggle"
            >
              <Icon name={s.contact_icons?.hours || "Clock"} className="w-3 h-3" />
              <span>{status.label}</span>
            </button>
            {hoursOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden z-40" data-testid="mobile-hours-dropdown">
                <HoursDropdownContent s={s} todayIdx={todayIdx} status={status} />
              </div>
            )}
          </div>
          )}

          {/* Account icon (shop enabled) */}
          {s.shop_enabled && showAccount && (
            <Link
              to={isAuthenticated ? "/account" : "/account/login"}
              className="relative inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full border border-[hsl(var(--border))] text-[color:var(--brand-navy)] hover:bg-[hsl(var(--muted))] transition-colors flex-shrink-0"
              aria-label={isAuthenticated ? `Mon compte (${customer?.first_name || customer?.email})` : "Se connecter"}
              data-testid="header-account-btn"
              title={isAuthenticated ? `Bonjour ${customer?.first_name || customer?.email}` : "Mon compte"}
            >
              <User className="w-4 h-4" strokeWidth={2.2} />
              {isAuthenticated && (
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white"></span>
              )}
            </Link>
          )}

          {/* Mini cart button with total */}
          {s.shop_enabled && showCart && (
            <button
              type="button"
              onClick={() => setCartOpen(true)}
              className="relative inline-flex items-center gap-2 w-9 h-9 justify-center md:w-auto md:h-10 md:px-3 rounded-full border border-[hsl(var(--border))] text-[color:var(--brand-navy)] hover:bg-[hsl(var(--muted))] transition-colors flex-shrink-0"
              aria-label={`Panier ${cartCount} article${cartCount > 1 ? "s" : ""} · ${(subtotal_cents/100).toFixed(2)} €`}
              data-testid="header-cart-btn"
            >
              <ShoppingCart className="w-4 h-4" strokeWidth={2.2} />
              <span className="hidden md:inline text-xs font-bold tabular-nums" data-testid="header-cart-total">
                {(subtotal_cents / 100).toFixed(2)} {s.shop_currency_symbol || "€"}
              </span>
              {cartCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--brand-red)] text-white text-[10px] font-bold flex items-center justify-center shadow"
                  data-testid="header-cart-count"
                >
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </button>
          )}

          {ctaCfg.enabled !== false && ctaCfg.label && ctaCfg.to && (
            <Link to={ctaCfg.to} className="hidden md:block" data-testid="header-devis-cta">
              <Button className={`rounded-full font-semibold ${ctaStyles[ctaCfg.style] || ctaStyles.sky}`}>
                {ctaCfg.label}
              </Button>
            </Link>
          )}

          <button
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border border-[hsl(var(--border))] text-[color:var(--brand-navy)]"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile expanded menu */}
      {open && (
        <div className="lg:hidden border-t border-[hsl(var(--border))] bg-white animate-fade-up" data-testid="mobile-menu">
          {/* Mobile hours chip (visible only below sm where we hid the header pill) */}
          <div className="sm:hidden px-4 pt-3">
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-semibold ${
              status.open ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-sky-300 bg-sky-50 text-sky-700"
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${status.open ? "bg-emerald-500" : "bg-sky-500"}`} />
              <span>{status.label}</span>
            </div>
          </div>
          {/* Quick contact icons row */}
          <div className="px-4 py-3 border-b border-[hsl(var(--border))] grid grid-cols-2 gap-2">
            {s.phone && (
              <a
                href={`tel:${s.phone.replace(/\s/g, "")}`}
                onClick={() => track("tel_click", { meta: { location: "mobile-menu" } })}
                className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[color:var(--brand-red)] text-white font-bold text-sm active:scale-95 transition-transform"
                data-testid="mobile-menu-phone"
              >
                <Icon name={s.cta_icons?.phone || "Phone"} className="w-4 h-4" strokeWidth={2.5} />
                <span>Appeler</span>
              </a>
            )}
            {s.email && (
              <a
                href={`mailto:${s.email}`}
                onClick={() => track("email_click", { meta: { location: "mobile-menu" } })}
                className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[color:var(--brand-sky)] text-white font-bold text-sm active:scale-95 transition-transform"
                data-testid="mobile-menu-email"
              >
                <Icon name={s.cta_icons?.message || s.contact_icons?.email || "Mail"} className="w-4 h-4" strokeWidth={2.5} />
                <span>Email</span>
              </a>
            )}
          </div>
          <nav className="px-4 py-3 flex flex-col gap-1">
            {nav.map((n, idx) => {
              const hasChildren = Array.isArray(n.children) && n.children.length > 0;
              if (hasChildren) {
                return (
                  <details key={n.id || n.to || idx} className="rounded-lg hover:bg-[hsl(var(--muted))]/30">
                    <summary className="px-4 py-3 rounded-lg text-sm font-medium cursor-pointer flex items-center justify-between">
                      <span>{n.label}</span>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </summary>
                    <div className="pl-4 py-1">
                      {n.to && (
                        <NavLink to={n.to} className="block px-4 py-2 text-xs text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]">
                          Voir {n.label}
                        </NavLink>
                      )}
                      {n.children.map((c, j) => (
                        <NavLink key={j} to={c.to} className={({ isActive }) => `block px-4 py-2 rounded-lg text-sm ${isActive ? "text-[color:var(--brand-navy)] font-semibold" : "text-[hsl(var(--foreground))]/80"}`}>
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </details>
                );
              }
              return (
                <NavLink
                  key={n.id || n.to || idx} to={n.to} end={n.to === "/"}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-lg text-sm font-medium ${isActive ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-[hsl(var(--muted))]"}`
                  }
                >
                  {n.label}
                </NavLink>
              );
            })}
            {ctaCfg.enabled !== false && ctaCfg.label && ctaCfg.to && (
              <Link
                to={ctaCfg.to}
                className={`mt-2 px-4 py-3 rounded-lg text-sm font-bold text-center ${ctaStyles[ctaCfg.style] || ctaStyles.sky}`}
                data-testid="mobile-menu-devis"
              >
                {ctaCfg.label}
              </Link>
            )}
            <details className="mt-2 px-4 py-2 rounded-lg bg-[hsl(var(--muted))]/50">
              <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${status.open ? "bg-emerald-500" : "bg-sky-500"}`} />
                {status.label}
                <ChevronDown className="w-3 h-3 ml-auto" />
              </summary>
              <ul className="mt-3 space-y-1.5 text-xs font-body">
                {(s.hours || []).map((h, i) => (
                  <li key={i} className={`flex justify-between ${i === todayIdx ? "font-semibold text-[color:var(--brand-navy)]" : "font-normal"}`}>
                    <span>{h.day}</span>
                    <span className="tabular-nums text-[hsl(var(--muted-foreground))]">{h.closed ? "Fermé" : `${h.morning || ""} ${h.afternoon ? "· " + h.afternoon : ""}`.trim()}</span>
                  </li>
                ))}
              </ul>
            </details>
          </nav>
        </div>
      )}
    </header>
  );
}

function HoursDropdown({ s, todayIdx, status }) {
  return (
    <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden animate-fade-up" data-testid="header-hours-dropdown">
      <HoursDropdownContent s={s} todayIdx={todayIdx} status={status} />
    </div>
  );
}


function DropdownNavItem({ item }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="px-4 py-2 rounded-full text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] inline-flex items-center gap-1 transition-all"
      >
        {item.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 min-w-[220px] bg-white rounded-xl shadow-xl border border-[hsl(var(--border))] overflow-hidden z-50 animate-fade-up">
          {item.to && (
            <NavLink to={item.to} onClick={() => setOpen(false)} className="block px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-[color:var(--brand-sky)] hover:bg-[hsl(var(--muted))]/40 border-b">
              Voir {item.label}
            </NavLink>
          )}
          {item.children.map((c, i) => (
            <NavLink
              key={i} to={c.to} onClick={() => setOpen(false)}
              className={({ isActive }) => `block px-4 py-2.5 text-sm transition-colors ${isActive ? "bg-[color:var(--brand-navy)]/5 text-[color:var(--brand-navy)] font-semibold" : "hover:bg-[hsl(var(--muted))]/40"}`}
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
